/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { WarningOutlined } from "@ant-design/icons";

import {
  Radio,
  Typography,
  Modal,
  Form,
  Select,
  Spin,
  Checkbox,
  Input,
  Button,
} from "antd";

import { LoadingOutlined, StarFilled } from "@ant-design/icons";
import { HugeRTEWrapper } from '@veritas/huge-rte-wrapper'

import { CaseStatusEnum } from "../../../constants";
import { useStatusUpdateModal } from "../../../hooks/useStatusUpdateModal";

import { useCaseViewContext } from "../../../context";

const { Option } = Select;
const { Text } = Typography;

const MIN_EXPLANATION_CHARS = 3;
const getVisibleTextLength = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return div.textContent.trim().length;
};

export const UpdateStatusModal = ({
  modalVisibility,
  onCancel,
  onConfirm,
  actionCaseStatus,
}) => {

  const { 
    apiClasses,
    caseDetails, 
    activeRequest,
    provider 
  } = useCaseViewContext()

  const { 
    form, 
    CaseUpdateStatusState,
    PROVIDER_NEEDS_REVIEW,
    user,
    requiresInformation,
    setCaseUpdateStatusState,
    compileHandlebars,
    handleRequireInformationChange,
    handleTemplateSelectionChange,
    handleRejectionReasonChange,
    handlePatientExplanationChange,
    handleStatusUpdate,
    editorRef,
  } = useStatusUpdateModal({
    modalVisibility,
    onCancel,
    onConfirm,
    actionCaseStatus,
    apiClasses,
    caseDetails, 
    activeRequest,
    provider,
  });

  const handleOnCancel = () => {
    form.resetFields()
    onCancel()
  }

  const handleClearPatientExplanation = () => {
    form.resetFields(['patientExplanation'])
    setCaseUpdateStatusState((prev) => ({
      ...prev,
      patientExplanation: '',
    }))
  }

  const nextStatusIsReferButNoRejectionReason = actionCaseStatus === CaseStatusEnum.REFER && !CaseUpdateStatusState.rejectionReason
  const nextStatusIsNeedsInfoButSpecificStatusNotSelected = !CaseUpdateStatusState.requireInformationStatus && actionCaseStatus === CaseStatusEnum.ADDITIONAL_INFO_REQUIRED
  const sendingExplanationButTooShort =
    CaseUpdateStatusState.sendInformationCheckBox &&
    getVisibleTextLength(CaseUpdateStatusState.patientExplanation) < MIN_EXPLANATION_CHARS

  return (
    <Modal
      open={modalVisibility}
      destroyOnClose={true}
      onOk={handleStatusUpdate}
      onCancel={handleOnCancel}
      width="35vw"
      okButtonProps={{
        disabled:
          nextStatusIsReferButNoRejectionReason ||
          nextStatusIsNeedsInfoButSpecificStatusNotSelected ||
          sendingExplanationButTooShort,
      }}
      okText={
        PROVIDER_NEEDS_REVIEW && actionCaseStatus === CaseStatusEnum.REFER
          ? "Refer"
          : "Update Status"
      }
    >
      <Spin
        spinning={CaseUpdateStatusState.modalload}
        indicator={<LoadingOutlined />}
      >
        <Form form={form} name="update case status modal">
          {actionCaseStatus !== CaseStatusEnum.ADDITIONAL_INFO_REQUIRED ? (
            <Form.Item
              label="Selected Case Status"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              
              <Text
                style={{ textTransform: "capitalize", fontWeight: "bolder" }}
              >
                {actionCaseStatus}
              </Text>
              { caseDetails?.status === CaseStatusEnum.CONSULT_COMPLETE && (
                  <div style={{ marginTop: '5px'}}>
                    <Text>
                      <WarningOutlined
                          style={{ color: "red", fontSize: "20px", marginRight: "5px" }}
                      />
                      This case is currently prescribed. Please be sure you've canceled all outstanding prescriptions before changing the status to Consult Complete No Rx
                    </Text>
                  </div>
              )}
            </Form.Item>
          ) : (
            <Form.Item
              label="Case Status"
              name="case status"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              required={true}
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                id="update-status-needs-info-select"
                size="large"
                style={{ textTransform: "capitalize" }}
                value={CaseUpdateStatusState.requireInformationStatus}
                placeholder="Select Case Status"
                onChange={handleRequireInformationChange}
              >
                {requiresInformation &&
                  requiresInformation.map((eachStatus) => (
                    <Option
                      key={eachStatus._id}
                      value={eachStatus.name}
                      style={{ textTransform: "capitalize" }}
                    >
                      {eachStatus.displayName}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Select a template message as patient explanation"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Radio.Group
              id="update-status-template-filter-group"
              value={CaseUpdateStatusState.templateFilter}
              onChange={({ target }) => {
                setCaseUpdateStatusState((prev) => ({
                  ...prev,
                  selectedTemplateMessage: "",
                  templateFilter: target.value,
                }));
              }}
            >
              <Radio.Button id="update-status-template-filter-all-radio" value="All">All</Radio.Button>
              <Radio.Button id="update-status-template-filter-my-radio" value="My">My</Radio.Button>
              <Radio.Button id="update-status-template-filter-shared-radio" value="Shared">Shared</Radio.Button>
              <Radio.Button id="update-status-template-filter-admin-radio" value="Admin">Admin</Radio.Button>
            </Radio.Group>
            <Select
              id="update-status-template-select"
              size="large"
              placeholder="Add template message as expanation to patient"
              onChange={handleTemplateSelectionChange}
              showSearch
              value={null}
              filterOption={(input, option) => {
                return (
                  option.key.toLowerCase().includes(input.toLowerCase()) ||
                  option.value.toLowerCase().includes(input.toLowerCase())
                );
              }}
              disabled={CaseUpdateStatusState.disableMessage}
            >
              {CaseUpdateStatusState.commonTemplateMessages
                .filter((template) => {
                  switch (CaseUpdateStatusState.templateFilter) {
                    case "Shared":
                      return (
                        !!template.ownerId &&
                        template.ownerId?._id !== user?._id
                      );
                    case "Admin":
                      return !template.ownerId;
                    case "My":
                      return template.ownerId?._id === user?._id;
                    default:
                      return true;
                  }
                })
                .map((eachTemplateMessage, index) => (
                  <Option
                    key={`${eachTemplateMessage.title}-${index}`}
                    value={compileHandlebars(eachTemplateMessage.message)}
                  >
                    {eachTemplateMessage.favoritedBy && (
                      <StarFilled style={{ color: "#7BAD7E" }} />
                    )}
                    {eachTemplateMessage.title}
                  </Option>
                ))}
              <Option key="custom-message" value={""}>
                Custom Message
              </Option>
            </Select>
          </Form.Item>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Form.Item
              name="Patient Explanation"
              label={
                <span style={{ color: "#7bad7b", fontWeight: "bold" }}>
                  Explanation To Patient
                </span>
              }
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Checkbox
                id="update-status-send-explanation-checkbox"
                checked={CaseUpdateStatusState.sendInformationCheckBox}
                onChange={({ target }) => {
                  if (!target.checked) {
                    form.resetFields(['patientExplanation'])
                    setCaseUpdateStatusState((prev) => ({
                      ...prev,
                      sendInformationCheckBox: false,
                      patientExplanation: '',
                      selectedTemplateMessage: '',
                    }))
                  } else {
                    setCaseUpdateStatusState((prev) => ({
                      ...prev,
                      sendInformationCheckBox: true,
                    }))
                  }
                }}
              >
                Send explanation to patient
              </Checkbox>
            </Form.Item>
            <Button id="update-status-clear-explanation-btn" onClick={handleClearPatientExplanation}>
              Clear
            </Button>
          </div>
          {CaseUpdateStatusState.sendInformationCheckBox && (
            <Form.Item name="patientExplanation">
              <HugeRTEWrapper
                ref={editorRef}
                disabled={CaseUpdateStatusState.disableMessage}
                onChange={handlePatientExplanationChange}
              />
            </Form.Item>
          )}
          {actionCaseStatus === CaseStatusEnum.REFER && (
            <Form.Item
              label={
                <span style={{ color: "#7bad7e", fontWeight: "bold" }}>
                  Rejection Reason (to be provided to reviewing physician)
                </span>
              }
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              rules={[{ required: true }]}
            >
              <Input.TextArea
                id="update-status-rejection-reason-textarea"
                name="Rejection Reason"
                value={CaseUpdateStatusState.rejectionReason}
                disabled={CaseUpdateStatusState.disableMessage}
                onChange={handleRejectionReasonChange}
              />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};